import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send } from 'lucide-react';
import { signUp } from '../api';
import { AuthCard } from '../components/AuthCard';

// The public sign-up page: a company asks for its own ActiveOptics workspace.
// Nothing is created yet. The request waits for approval; once approved, the
// person who asked is emailed an invite to set their password and becomes the
// workspace's first admin.

const TEAM_SIZES = [
  { value: '', label: 'Choose one (optional)' },
  { value: '1-5', label: '1 to 5 people' },
  { value: '6-20', label: '6 to 20 people' },
  { value: '21-50', label: '21 to 50 people' },
  { value: '51+', label: 'More than 50' },
];

const EMPTY = { company: '', name: '', email: '', team_size: '', message: '', website: '' };
const DONE_MESSAGE = "Thanks. We'll review your request and email you an invite to set up your workspace once it's approved.";

function SignUp() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      company: form.company.trim(),
      name: form.name.trim(),
      email: form.email.trim(),
      team_size: form.team_size,
      message: form.message.trim(),
      website: form.website,
    };
    if (!payload.company || !payload.name || !payload.email) {
      setError('Enter your company, your name and your work email.');
      return;
    }
    setSending(true);
    try {
      const res = await signUp(payload);
      setDone(res.data?.message || DONE_MESSAGE);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send your request. Try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <AuthCard subtitle="Create your company's workspace">
      {done ? (
        <>
          <div className="alert alert-success" role="status">{done}</div>
          <p className="login-text">The invite comes from ActiveOptics to the email you gave. If nothing arrives after a day or two, check your spam folder.</p>
        </>
      ) : (
        <>
          <p className="login-text">
            Tell us about your company. Each company gets its own private workspace, and you become its first admin.
            We review every request and email you an invite once it's approved.
          </p>
          {error && <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>}
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label className="form-label" htmlFor="signup-company">Company *</label>
              <input id="signup-company" type="text" className="form-input" value={form.company} onChange={set('company')}
                maxLength={120} autoComplete="organization" required aria-required="true" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="signup-name">Your name *</label>
              <input id="signup-name" type="text" className="form-input" value={form.name} onChange={set('name')}
                maxLength={100} autoComplete="name" required aria-required="true" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="signup-email">Work email *</label>
              <input id="signup-email" type="email" className="form-input" value={form.email} onChange={set('email')}
                maxLength={200} autoComplete="email" required aria-required="true" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="signup-team">Team size</label>
              <select id="signup-team" className="form-input" value={form.team_size} onChange={set('team_size')}>
                {TEAM_SIZES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="signup-message">Anything we should know?</label>
              <textarea id="signup-message" className="form-input" rows={3} value={form.message} onChange={set('message')} maxLength={1000} />
            </div>
            {/* Left empty by people; bots fill it in. Hidden from view and from screen readers. */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}>
              <label htmlFor="signup-website">Website</label>
              <input id="signup-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
            </div>
            <button type="submit" className="btn btn-primary login-btn" disabled={sending}>
              {sending ? <span>Sending...</span> : (<><Send size={18} /><span>Request my workspace</span></>)}
            </button>
          </form>
        </>
      )}
      <p className="login-request">Already have an account? <Link to="/login">Sign in</Link></p>
    </AuthCard>
  );
}

export default SignUp;
